import React, { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import {
  Download,
  Upload,
  CheckCircle,
  AlertCircle,
  FileSpreadsheet,
  Loader2,
  Info,
  X,
  Check,
} from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import * as XLSX from 'xlsx'

interface ExcelEmployee {
  sicil: string
  personel: string
  satis_direktoru?: string
  satis_muduru?: string
  bm_by_bs?: string
  pozisyon?: string
  mail_adresi: string
  kod?: string
  magaza?: string
}

interface ValidationError {
  row: number
  field: string
  message: string
}

interface EmployeeExcelUpdateModalProps {
  open: boolean
  setOpen: (open: boolean) => void
  onEmployeesUpdated: () => void
}

const EmployeeExcelUpdateModal: React.FC<EmployeeExcelUpdateModalProps> = ({
  open,
  setOpen,
  onEmployeesUpdated,
}) => {
  const [file, setFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<ValidationError[]>([])
  const [success, setSuccess] = useState<number>(0)
  const [processed, setProcessed] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentStep, setCurrentStep] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [processedRows, setProcessedRows] = useState(0)

  const exampleData = [
    {
      sicil: '12345',
      personel: 'Ahmet Yılmaz',
      satis_direktoru: 'Mehmet Kaya',
      satis_muduru: 'Ayşe Demir',
      bm_by_bs: 'BM001',
      pozisyon: 'Satış Danışmanı',
      mail_adresi: 'ahmet.yilmaz@company.com',
      kod: 'AY001',
      magaza: 'İstanbul Merkez',
    },
    {
      sicil: '67890',
      personel: 'Fatma Öz',
      satis_direktoru: 'Ali Vural',
      satis_muduru: 'Zeynep Ak',
      bm_by_bs: 'BM002',
      pozisyon: 'Müdür',
      mail_adresi: 'fatma.oz@company.com',
      kod: 'FO001',
      magaza: 'Ankara Çankaya',
    },
  ]

  const mandatoryFields = [
    { key: 'sicil', label: 'Sicil No' },
    { key: 'personel', label: 'Personel Adı' },
    { key: 'mail_adresi', label: 'Mail Adresi' },
  ]

  const optionalFields = [
    { key: 'satis_direktoru', label: 'Satış Direktörü' },
    { key: 'satis_muduru', label: 'Satış Müdürü' },
    { key: 'bm_by_bs', label: 'BM/BY/BS' },
    { key: 'pozisyon', label: 'Pozisyon' },
    { key: 'kod', label: 'Kod' },
    { key: 'magaza', label: 'Mağaza' },
  ]

  const validColumns = [
    'sicil',
    'personel',
    'satis_direktoru',
    'satis_muduru',
    'bm_by_bs',
    'pozisyon',
    'mail_adresi',
    'kod',
    'magaza',
  ]

  const downloadExampleFile = () => {
    const ws = XLSX.utils.json_to_sheet(exampleData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Çalışanlar')
    XLSX.writeFile(wb, 'calisan_guncelleme_ornegi.xlsx')
  }

  const validateEmployee = (employee: any, rowIndex: number): ValidationError[] => {
    const errors: ValidationError[] = []

    // Required fields
    if (!employee.sicil || String(employee.sicil).trim() === '') {
      errors.push({
        row: rowIndex,
        field: 'sicil',
        message: 'Sicil alanı zorunludur',
      })
    }

    if (!employee.personel || String(employee.personel).trim() === '') {
      errors.push({
        row: rowIndex,
        field: 'personel',
        message: 'Personel alanı zorunludur',
      })
    }

    if (!employee.mail_adresi || String(employee.mail_adresi).trim() === '') {
      errors.push({
        row: rowIndex,
        field: 'mail_adresi',
        message: 'Mail adresi alanı zorunludur',
      })
    } else {
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(String(employee.mail_adresi))) {
        errors.push({
          row: rowIndex,
          field: 'mail_adresi',
          message: 'Geçerli bir mail adresi giriniz',
        })
      }
    }

    return errors
  }

  const validateExcelStructure = (jsonData: any[]): ValidationError[] => {
    const errors: ValidationError[] = []

    if (jsonData.length === 0) {
      errors.push({
        row: 0,
        field: 'file',
        message: 'Excel dosyası boş veya geçersiz format',
      })
      return errors
    }

    // Check for unknown columns
    const firstRow = jsonData[0]
    const fileColumns = Object.keys(firstRow)
    const unknownColumns = fileColumns.filter((col) => !validColumns.includes(col))

    if (unknownColumns.length > 0) {
      errors.push({
        row: 1,
        field: 'structure',
        message: `Geçersiz sütunlar bulundu: ${unknownColumns.join(', ')}. Geçerli sütunlar: ${validColumns.join(', ')}`,
      })
    }

    return errors
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0]
    if (!uploadedFile) return

    setFile(uploadedFile)
    setErrors([])
    setSuccess(0)
    setProcessed(false)
    setProgress(0)
    setCurrentStep('')
    setTotalRows(0)
    setProcessedRows(0)
  }

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

  const processExcelFile = async () => {
    if (!file) return

    setLoading(true)
    setErrors([])
    setSuccess(0)
    setProgress(0)
    setProcessedRows(0)

    try {
      setCurrentStep('Excel dosyası okunuyor...')
      setProgress(10)
      await sleep(300)

      const arrayBuffer = await file.arrayBuffer()
      const workbook = XLSX.read(arrayBuffer, { type: 'array' })
      const worksheet = workbook.Sheets[workbook.SheetNames[0]]
      const jsonData = XLSX.utils.sheet_to_json(worksheet)

      // Validate Excel structure first
      const structureErrors = validateExcelStructure(jsonData)
      if (structureErrors.length > 0) {
        setErrors(structureErrors)
        setLoading(false)
        setCurrentStep('')
        setProgress(0)
        return
      }

      setTotalRows(jsonData.length)
      setCurrentStep('Veriler doğrulanıyor...')
      setProgress(20)
      await sleep(300)

      const allErrors: ValidationError[] = []
      const validEmployees: ExcelEmployee[] = []
      const seenSicils = new Set<string>()

      // Validate all rows
      jsonData.forEach((row: any, index) => {
        const rowErrors = validateEmployee(row, index + 2)
        allErrors.push(...rowErrors)

        if (rowErrors.length === 0) {
          const sicil = String(row.sicil).trim()

          // Check for duplicates in Excel file
          if (seenSicils.has(sicil)) {
            allErrors.push({
              row: index + 2,
              field: 'sicil',
              message: `Sicil numarası ${sicil} Excel dosyasında birden fazla kez kullanılmış`,
            })
            return
          }
          seenSicils.add(sicil)

          validEmployees.push({
            sicil,
            personel: String(row.personel).trim(),
            satis_direktoru: row.satis_direktoru ? String(row.satis_direktoru).trim() : undefined,
            satis_muduru: row.satis_muduru ? String(row.satis_muduru).trim() : undefined,
            bm_by_bs: row.bm_by_bs ? String(row.bm_by_bs).trim() : undefined,
            pozisyon: row.pozisyon ? String(row.pozisyon).trim() : undefined,
            mail_adresi: String(row.mail_adresi).trim(),
            kod: row.kod ? String(row.kod).trim() : undefined,
            magaza: row.magaza ? String(row.magaza).trim() : undefined,
          })
        }
      })

      if (allErrors.length > 0) {
        setErrors(allErrors)
        setLoading(false)
        setCurrentStep('')
        setProgress(0)
        return
      }

      setCurrentStep('Çalışanlar güncelleniyor...')
      setProgress(40)

      // Update employees in database
      let successCount = 0
      const updateErrors: ValidationError[] = []

      for (let i = 0; i < validEmployees.length; i++) {
        const employee = validEmployees[i]

        try {
          const { data, error } = await supabase
            .from('employee')
            .update({
              personel: employee.personel,
              satis_direktoru: employee.satis_direktoru,
              satis_muduru: employee.satis_muduru,
              bm_by_bs: employee.bm_by_bs,
              pozisyon: employee.pozisyon,
              mail_adresi: employee.mail_adresi,
              kod: employee.kod,
              magaza: employee.magaza,
            })
            .eq('sicil', employee.sicil)
            .select()

          if (error) {
            updateErrors.push({
              row: i + 2,
              field: 'sicil',
              message: `Güncelleme hatası: ${error.message}`,
            })
          } else if (data && data.length === 0) {
            updateErrors.push({
              row: i + 2,
              field: 'sicil',
              message: `Sicil numarası ${employee.sicil} bulunamadı`,
            })
          } else {
            successCount++
          }
        } catch (error) {
          updateErrors.push({
            row: i + 2,
            field: 'sicil',
            message: `Beklenmeyen hata: ${error}`,
          })
        }

        // Update progress
        const currentProgress = 40 + ((i + 1) / validEmployees.length) * 50
        setProgress(currentProgress)
        setProcessedRows(i + 1)
        setCurrentStep(`Güncelleniyor... (${i + 1}/${validEmployees.length})`)

        // Small delay to show progress
        if (i % 5 === 0) {
          await sleep(100)
        }
      }

      setErrors(updateErrors)
      setSuccess(successCount)
      setProcessed(true)
      setProgress(100)
      setCurrentStep('Tamamlandı!')

      if (successCount > 0) {
        onEmployeesUpdated()
      }
    } catch (error) {
      console.error('Excel processing error:', error)
      setErrors([
        {
          row: 0,
          field: 'file',
          message: 'Excel dosyası işlenirken hata oluştu',
        },
      ])
      setCurrentStep('Hata oluştu!')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (loading) return // Prevent closing while processing

    setFile(null)
    setErrors([])
    setSuccess(0)
    setProcessed(false)
    setProgress(0)
    setCurrentStep('')
    setTotalRows(0)
    setProcessedRows(0)
    setOpen(false)
  }

  return (
    <Dialog open={open} onOpenChange={!loading ? handleClose : undefined}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader className="pb-2">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-6 h-6 text-blue-600" />
            <DialogTitle className="text-xl">Excel ile Çalışan Güncelleme</DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-6 overflow-y-auto max-h-[calc(90vh-120px)] pr-2">
          {/* Field Requirements */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-medium text-green-900 mb-3 flex items-center gap-2">
                <Check className="w-4 h-4" />
                Zorunlu Alanlar
              </h4>
              <div className="space-y-2">
                {mandatoryFields.map((field) => (
                  <Badge
                    key={field.key}
                    variant="secondary"
                    className="bg-green-100 text-green-800"
                  >
                    {field.label}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h4 className="font-medium text-gray-900 mb-3">İsteğe Bağlı Alanlar</h4>
              <div className="flex flex-wrap gap-2">
                {optionalFields.map((field) => (
                  <Badge key={field.key} variant="outline" className="text-gray-600">
                    {field.label}
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          {/* Download Example */}
          <div className="flex justify-center">
            <Button
              variant="outline"
              onClick={downloadExampleFile}
              className="gap-2 bg-white hover:bg-gray-50"
              disabled={loading}
            >
              <Download className="w-4 h-4" />
              Örnek Excel Dosyasını İndir
            </Button>
          </div>

          {/* File Upload */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700">Excel Dosyası Seçin</label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                disabled={loading}
                className="max-w-xs mx-auto"
              />
              {file && (
                <div className="mt-2 text-sm text-gray-600">
                  Seçilen dosya: <span className="font-medium">{file.name}</span>
                </div>
              )}
            </div>
          </div>

          {/* Process Button */}
          {file && !processed && (
            <div className="flex justify-center">
              <Button
                onClick={processExcelFile}
                disabled={loading}
                className="gap-2 px-8 py-2 bg-blue-600 hover:bg-blue-700"
                size="lg"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    İşleniyor...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Çalışanları Güncelle
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Progress Bar */}
          {loading && (
            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">{currentStep}</span>
                  <span className="text-sm text-gray-500">{Math.round(progress)}%</span>
                </div>
                <Progress value={progress} className="h-2" />
                {totalRows > 0 && (
                  <div className="text-xs text-gray-500 text-center">
                    {processedRows} / {totalRows} kayıt işlendi
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Success Message */}
          {processed && success > 0 && (
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-medium text-green-800">İşlem Başarılı!</span>
              </div>
              <p className="text-green-700 mt-1">{success} çalışan başarıyla güncellendi.</p>
            </div>
          )}

          {/* Errors */}
          {errors.length > 0 && (
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <span className="font-medium text-red-800">Hatalar ({errors.length})</span>
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto bg-white p-3 rounded border">
                {errors.map((error, index) => (
                  <div key={index} className="text-sm text-red-700 flex items-start gap-2">
                    <X className="w-3 h-3 text-red-500 mt-0.5 flex-shrink-0" />
                    <span>
                      <strong>Satır {error.row}:</strong> {error.message}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {processed && (
            <div className="flex justify-center gap-3 pt-4">
              <Button variant="outline" onClick={handleClose}>
                Kapat
              </Button>
              <Button
                onClick={() => {
                  setFile(null)
                  setErrors([])
                  setSuccess(0)
                  setProcessed(false)
                  setProgress(0)
                  setCurrentStep('')
                  setTotalRows(0)
                  setProcessedRows(0)
                }}
                className="gap-2"
              >
                <Upload className="w-4 h-4" />
                Yeni Dosya Yükle
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default EmployeeExcelUpdateModal
